import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || window.location.origin)
  .replace('https://', 'wss://')
  .replace('http://', 'ws://');

export function useWebSocket(onMessage) {
  const { token, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimeout = useRef(null);
  const keepAliveInterval = useRef(null);

  const connect = useCallback(() => {
    if (!token || !isAuthenticated) return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(`${WS_BASE}/api/ws/${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Keep-alive ping every 45s
      keepAliveInterval.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 45000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Ignore pings/pongs, forward real notifications
        if (data.type !== 'pong' && data.type !== 'ping' && onMessage) {
          onMessage(data);
        }
      } catch (err) {
        console.error('WebSocket message parse error:', err);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (keepAliveInterval.current) clearInterval(keepAliveInterval.current);
      // Reconnect seulement si on est encore authentifié au moment de la fermeture
      // On utilise wsRef pour éviter la stale closure sur isAuthenticated
      reconnectTimeout.current = setTimeout(() => {
        // Vérifie l'état actuel du token plutôt que la closure capturée
        if (wsRef.current === ws) {
          connect();
        }
      }, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, isAuthenticated, onMessage]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (keepAliveInterval.current) clearInterval(keepAliveInterval.current);
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  return { connected, sendMessage };
}
